/**
 * In-app first-session Aha demo (Feb 2026 — UX simplification pass).
 *
 * What changed
 * ------------
 * Was 3 steps: Welcome → Consent → Try-It demo (with consent collected TWICE
 *   — once here and once in /onboarding before this even mounted).
 * Now 1 step: just the 10-second "Try a recording" demo.
 *
 * Why
 * ---
 * After OnboardingPage compression, the user has already:
 *   - Picked subjects
 *   - Accepted consent
 *   - Seen a confetti and landed on /app
 * The old "Welcome to the future" copy + duplicate consent was friction that
 * pushed users past the moment of intent. The aha demo is the only piece
 * that delivers value on the first session, so it's now the only thing here.
 *
 * Behaviour
 * ---------
 * - Dismissible at any time (X in corner, "Skip for now" link).
 * - On skip OR finish, calls `/auth/complete-activation-flow` so it never
 *   shows again for this user (server tracks the flag).
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Sparkles, X } from 'lucide-react';
import { Button } from './ui/button';
import confetti from 'canvas-confetti';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnboardingFlow = ({ onComplete }) => {
  const { user } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const timerRef = useRef(null);
  const firstName = user?.name?.split(' ')[0] || 'Scholar';

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleRecordDemo = () => {
    setIsRecording(true);
    setRecordingTime(0);
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= 10) {
          clearInterval(timerRef.current);
          setIsRecording(false);
          setShowSummary(true);
          confetti({
            particleCount: 120,
            spread: 80,
            origin: { y: 0.7 },
            colors: ['#FFD700', '#002147', '#34D399', '#F59E0B'],
          });
          return 10;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const markComplete = async () => {
    try {
      await axios.post(`${API}/auth/complete-activation-flow`);
    } catch (e) {
      console.error('Failed to save activation status:', e);
    }
    onComplete();
  };

  const backdrop = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const modal = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', damping: 25, stiffness: 300 } },
    exit: { opacity: 0, scale: 0.95, y: 10 },
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        variants={backdrop}
        initial="hidden"
        animate="visible"
        exit="exit"
        data-testid="onboarding-overlay"
      >
        <div className="absolute inset-0 bg-[#002147]/60 dark:bg-black/60 backdrop-blur-sm" />
        <motion.div
          className="relative w-full max-w-lg bg-[var(--isp-glass)] backdrop-blur-2xl border border-[var(--isp-glass-border)] rounded-[24px] shadow-[0_20px_60px_rgba(0,33,71,0.2)] overflow-hidden"
          variants={modal}
          data-testid="onboarding-modal"
        >
          {/* Dismiss */}
          <button
            onClick={markComplete}
            className="absolute top-4 right-4 p-2 rounded-full text-[var(--isp-body)] hover:bg-black/5 transition-colors z-10"
            data-testid="onboarding-skip-btn"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="px-8 py-10 text-center" data-testid="onboarding-step-aha">
            {!showSummary ? (
              <>
                <div className="w-16 h-16 mx-auto mb-5 bg-gradient-to-br from-[#FFD700] to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/20 rounded-2xl">
                  <Mic className="w-8 h-8 text-[var(--isp-heading)]" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] mb-2">
                  Welcome, {firstName}.
                </h2>
                <p className="font-body text-[var(--isp-body)] leading-relaxed mb-6">
                  Try it in 10 seconds — tap the mic and say anything.
                  We&apos;ll show you the magic.
                </p>

                {isRecording ? (
                  <div className="space-y-4">
                    <div className="relative w-24 h-24 mx-auto">
                      <motion.div
                        className="absolute inset-0 rounded-full bg-[#FFD700]/20"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center rounded-full bg-[#FFD700] shadow-[0_0_30px_rgba(255,215,0,0.5)]">
                        <Mic className="w-10 h-10 text-[var(--isp-heading)]" />
                      </div>
                    </div>
                    <p className="font-heading text-4xl font-bold text-[var(--isp-heading)]">{recordingTime}s</p>
                    <p className="font-body text-sm text-[var(--isp-body)]">Our AI is listening...</p>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={handleRecordDemo}
                      className="w-24 h-24 mx-auto rounded-full bg-[#FFD700] hover:bg-[#F6D000] shadow-[0_0_30px_rgba(255,215,0,0.4)] flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                      data-testid="onboarding-record-btn"
                      aria-label="Try a 10-second recording demo"
                    >
                      <Mic className="w-10 h-10 text-[var(--isp-heading)]" />
                    </button>
                    <button
                      onClick={markComplete}
                      className="mt-4 text-sm text-[var(--isp-muted)] hover:text-[var(--isp-heading)] underline transition-colors"
                      data-testid="onboarding-skip-mic-btn"
                    >
                      Skip for now
                    </button>
                  </>
                )}
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', damping: 20 }}
              >
                <div className="w-16 h-16 mx-auto mb-5 bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-400/20 rounded-2xl">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] mb-3">
                  Boom — your study pack is ready.
                </h2>
                <p className="font-body text-[var(--isp-body)] leading-relaxed mb-6">
                  That&apos;s how fast it works. Record a real lecture and watch the magic unfold.
                </p>
                <div className="space-y-2 text-left mb-6">
                  {['3 Key Points extracted', '5 Flashcards generated', '1 AI Summary ready'].map((item, i) => (
                    <motion.div
                      key={`item-${i}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.15 }}
                      className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200/50"
                    >
                      <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="font-body text-sm text-emerald-800 font-medium">{item}</span>
                    </motion.div>
                  ))}
                </div>
                <Button
                  onClick={markComplete}
                  className="w-full bg-[#002147] hover:bg-[#00142A] text-white py-3 font-body font-semibold text-base gap-2"
                  data-testid="onboarding-finish-btn"
                >
                  Start Studying <Sparkles className="w-4 h-4" />
                </Button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingFlow;
